import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CriminalCertificate, DocumentReviewStatus } from '../../../../mocks/cases';
import CategoryHeader from './CategoryHeader';
import { CountrySelector, CriminalCertCard } from './CriminalHelpers';

interface Props {
  criminalCertificates: CriminalCertificate[];
  onCriminalCertificatesChange?: (updated: CriminalCertificate[]) => void;
  countriesOfResidence: string[];
  onCountriesChange?: (countries: string[]) => void;
  categoryReview: DocumentReviewStatus;
  onCategoryReviewChange: (status: DocumentReviewStatus) => void;
  onViewCertificate?: (country: string, cert: CriminalCertificate) => void;
}

export default function CriminalCategory({
  countriesOfResidence, onCountriesChange,
  criminalCertificates, onCriminalCertificatesChange,
  categoryReview, onCategoryReviewChange,
  onViewCertificate,
}: Props) {
  const { t } = useTranslation();
  const [warning, setWarning] = useState('');
  const hasIncompleteCountry = (country: string) => {
    const cert = criminalCertificates.find((c) => c.country === country);
    return !cert || (!cert.uploaded && !cert.certRequestedNotReceived);
  };

  const canAddAnotherCountry = countriesOfResidence.every((country) => country === 'Spain' || !hasIncompleteCountry(country));
  return (
    <div className="max-w-[720px]">
      <CategoryHeader title={t('advisorPortal.docCat_criminal_record_name')} category="criminal" status={categoryReview} onChange={onCategoryReviewChange} />

      <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-4 max-w-[480px]">
        <p className="text-[13px] font-semibold text-slate-800 mb-3">
          {t('advisorPortal.criminalCountriesPrompt', { defaultValue: 'Which countries has the applicant lived in?' })}
        </p>
        {warning ? (
          <p className="mb-2 text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            {warning}
          </p>
        ) : null}
        <div className="max-w-[420px]">
          <CountrySelector
            exclude={countriesOfResidence}
            onAdd={(country) => {
              if (!canAddAnotherCountry) {
                setWarning(
                  t('advisorPortal.criminalUploadCurrentFirst', {
                    defaultValue:
                      'Upload the current country certificate or mark it unavailable before adding another country.',
                  }),
                );
                return;
              }
              setWarning('');
              onCountriesChange?.([...countriesOfResidence, country]);
            }}
          />
        </div>
      </div>

      {countriesOfResidence.length > 0 && (
        <div className="space-y-2 max-w-[480px]">
          {countriesOfResidence.map((country) => {
            const cert = criminalCertificates.find((c) => c.country === country);
            return (
              <CriminalCertCard
                key={country}
                country={country}
                cert={cert}
                onUpdate={(changes) => {
                  if (cert) {
                    onCriminalCertificatesChange?.(
                      criminalCertificates.map((c) =>
                        c.id === cert.id ? { ...c, ...changes } : c,
                      ),
                    );
                  } else {
                    const newCert: CriminalCertificate = {
                      id: `crim-${country.replace(/[^a-z0-9]/gi, '').toLowerCase()}`,
                      country,
                      uploaded: false,
                      reviewStatus: 'pending',
                      certRequestedNotReceived: false,
                      ...changes,
                    };
                    onCriminalCertificatesChange?.([...criminalCertificates, newCert]);
                  }
                }}
                onRemove={() => {
                  onCountriesChange?.(countriesOfResidence.filter((c) => c !== country));
                  onCriminalCertificatesChange?.(
                    criminalCertificates.filter((c) => c.country !== country),
                  );
                }}
                onView={cert ? () => onViewCertificate?.(country, cert) : undefined}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

